/**
 * @file TripItineraryCard
 *
 * Component is used in both create and update flows.
 * Only change is which variant of AdditionalTripInfoBlock is passed in.
 */

import React from "react";
import moment from "moment-timezone";
import first from "lodash/first";

import { Box, Card, Divider, Typography } from "@mui/material";

import { useScreenSize, useTimeFormat } from "../../../../../globals/hooks";
import { dateFormatter } from "../../../../../globals/utils/helpers";
import { grayLight, moovsBlue, moovsBlueSelected } from "design-system/colors";
import {
  Airport,
  RoundTripVariant,
  TextRecipientEnum,
  TripCategory,
} from "../../../../../types";
import TripCancelledBanner from "../../../TripCancelledBanner";
import ExpiredTripBanner from "../../../../trip/ExpiredTripBanner";
import TripTimeline from "./components/TripTimeline/TripTimeline";
import TripItineraryHeader from "./components/TripItineraryHeader";
import TripMap from "./components/TripMap/TripMap";
import FlightChangeDisplay from "components/globals/FlightChangeDisplay";
import { ApolloQueryResult } from "@apollo/client";

type TripItineraryCardProps = {
  orderNumber?: string;
  refetch?: (
    variables?: Partial<{
      id: string;
    }>
  ) => Promise<ApolloQueryResult<any>>;
  trip: {
    id?: string;
    totalDuration?: number;
    estimatedDuration?: number;
    useTotalDuration?: boolean;
    totalGroupSize: number;
    tripCategory: TripCategory;
    roundTripVariant?: RoundTripVariant;
    cancelledAt?: Date;
    googleDirectionsResult?: {};
    tripNumber?: string;
    label?: string | null;
    isVehicleNotInUse?: boolean;
    charterServiceDate?: string;
    isMasked?: boolean;
    routes: {
      vehicle?: {};
      publicId?: string;
    }[];
    stops: {
      stopIndex: number;
      location: string;
      dateTime?: string;
      airport?: Airport;
      originalDateTime: Date;
      flightOffset: number;
      customLocationName?: string | null;
      customGooglePlusCode?: string | null;
    }[];
    statusUpdateTextTo?: TextRecipientEnum;
  };
  additionalTripInfoBlock: React.ReactNode;
  isQuote: boolean;
  mode: "update" | "create";
  hasReturnTrip?: boolean;
  isReturnTrip?: boolean;
};

function TripItineraryCard(props: TripItineraryCardProps) {
  const {
    trip,
    orderNumber,
    additionalTripInfoBlock,
    isQuote,
    mode,
    hasReturnTrip,
    isReturnTrip,
  } = props;

  const confirmationNumber = orderNumber && `${orderNumber}-${trip.tripNumber}`;
  const pickUpStop = first(trip.stops);
  const tripLabel = trip.label?.trim();
  const isStandbyDay =
    trip.tripCategory === TripCategory.Charter && trip.isVehicleNotInUse;

  // hooks
  const { isMobileView } = useScreenSize();
  const { getTimeFormat } = useTimeFormat();

  return (
    <>
      <Card variant="outlined">
        <Box m={3}>
          <TripItineraryHeader
            totalDuration={trip.totalDuration}
            estimatedDuration={trip.estimatedDuration}
            useTotalDuration={trip.useTotalDuration}
            totalGroupSize={trip.totalGroupSize}
            confirmationNumber={confirmationNumber}
            tripCategory={trip.tripCategory}
            roundTripVariant={trip.roundTripVariant}
            tripId={trip.id}
            statusUpdateTextTo={trip.statusUpdateTextTo}
            mode={mode}
            hasReturnTrip={hasReturnTrip}
            isReturnTrip={isReturnTrip}
          />
          {trip.isMasked && (
            <Box mt={3}>
              <ExpiredTripBanner />
            </Box>
          )}
          {trip.cancelledAt && (
            <Box mt={3}>
              <TripCancelledBanner cancelledAt={trip.cancelledAt} />
            </Box>
          )}
          {!!tripLabel && (
            <Box
              mt={1.5}
              display="inline-flex"
              maxWidth="100%"
              bgcolor={moovsBlueSelected}
              border={`1px solid ${moovsBlue}`}
              borderRadius="4px"
              px={1.5}
              py={0.5}
            >
              <Typography
                variant="body2"
                color={moovsBlue}
                fontWeight={700}
                noWrap
              >
                {tripLabel}
              </Typography>
            </Box>
          )}
          <Box
            mt={2}
            mb={1}
            display="flex"
            flexDirection={isMobileView ? "column" : "row"}
            justifyContent="space-between"
          >
            <Box width="100%">
              <Typography variant="overline" style={{ marginBottom: "6px" }}>
                {isStandbyDay ? "DATE" : "PICK-UP DATE & TIME"}
              </Typography>
              <Box display="flex" flexDirection="row">
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  {isStandbyDay
                    ? trip.charterServiceDate
                      ? dateFormatter(moment.utc(trip.charterServiceDate), "medium")
                      : "Standby Day"
                    : dateFormatter(
                        moment.utc(first(trip.stops).dateTime),
                        "medium",
                        {
                          mode: "dateTime",
                          timeFormat: getTimeFormat(),
                        }
                      )}
                </Typography>
                {!isStandbyDay && (
                  <FlightChangeDisplay
                    flightOffset={pickUpStop?.flightOffset}
                    originalDateTime={pickUpStop?.originalDateTime}
                    dateTime={pickUpStop?.dateTime}
                  />
                )}
              </Box>

              {/* trip timeline */}
              <Box mt={4.5}>
                {isStandbyDay ? (
                  <Box bgcolor={grayLight} py={3} textAlign="center">
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                      Vehicle Not In Use
                    </Typography>
                    <Typography variant="body2">Standby Day</Typography>
                  </Box>
                ) : (
                  <TripTimeline trip={trip} isQuote={isQuote} />
                )}
              </Box>
            </Box>

            {/* map */}
            {!isStandbyDay && (
              <Box width="100%" {...(isMobileView ? { mb: 4 } : { ml: 8 })}>
                <TripMap trip={trip} />
              </Box>
            )}
          </Box>
          <Divider />

          {/* additional trip info */}
          <Box mt={2}>{additionalTripInfoBlock}</Box>
        </Box>
      </Card>
    </>
  );
}

export default TripItineraryCard;
